import React, { useEffect, useMemo, useState } from "react";
import { SectionWrapper } from "../components/SectionWrapper";
import { Button } from "../components/Button";
import { IState } from "../features/profileAccountHook";
import { Switch } from "../components/Swtich";
import ProfileService from "@services/Profile.service";
import moment from "moment";
import classNames from "classnames";
import DesktopIcon from "../icons/desktop-device.svg";
import MobileIcon from "../icons/mobile-device.svg";
import UnlockIcon from "../icons/unlock.svg";
import ChevronDownIcon from "../icons/chevron-down.svg";
import { Input, notification, Table } from "antd";
import {
  getSeurityActivitiesDataSource,
  securityActivitiesColumns,
} from "../const";
import { TwoFAModal } from "../components/Modals/TwoFAModal";
import SecurityService from "@services/Security.service";
import { use2FAFieldLogic } from "../hooks/use2FAFieldLogic";
import { ChangePasswordModal } from "../components/Modals/ChangePasswordModal";
import { TwoFABackupModal } from "../components/Modals/TwoFABackupModal";
import Loader from "@appComponents/Loader/Loader";
import { useAccountPageContext } from "../AcountPage.context";

const paginationDefaultValue = 4;

export const Security = () => {
  const [is2FaChecked, setIs2FaChecked] = useState(false);
  const [devices, setDevices] = useState([]);
  const [activities, setActivities] = useState([]);
  const [visibleItems, setVisibleItems] = useState(paginationDefaultValue);
  const [is2FAModdalOpen, setIs2FAModdalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isRegenerateCodesModalOpen, setIsRegenerateCodesModalOpen] =
    useState(false);
  const [regeneratedBackupCodes, setRegeneratedBackupCodes] = useState([]);

  const {
    is2FAEnabled,
    is2FALoading,
    is2FALocked,
    setIs2FAEnabled,
    setIs2FALoading,
    setIs2FALocked,
  } = useAccountPageContext();

  const {
    inputRefs: twoFAInputRefs,
    pin: twoFAPin,
    handleChange: twoFAHandleChange,
    handleKeyDown: twoFAHandleKeyDown,
    handleClearPin: twoFAHandleClearPin,
    handlePaste: twoFAHandlePaste,
  } = use2FAFieldLogic();

  const securityHistoryData = useMemo(() => {
    return getSeurityActivitiesDataSource(activities);
  }, [activities]);

  const handleToggle2Fa = (enabled: boolean) => {
    if (is2FALocked) {
      return;
    }

    if (enabled) {
      setIs2FAModdalOpen(true);
    } else {
      SecurityService.twoFaDisable().then(() => {
        fetch2FAStatus();
      });

      setIs2FaChecked(false);
    }
  };

  const handleUnlock2FA = (pin?: string) => {
    SecurityService.twoFaUnlock({ code_2fa: pin ?? twoFAPin.join("") })
      .then(() => {
        setIs2FALocked(false);
        twoFAHandleClearPin();
      })
      .catch((err) => {
        if (typeof err.response.data === "string") {
          notification.error({
            message: err.response.data,
          });
        }

        twoFAHandleClearPin();
      });
  };

  const handleRegenerateCodes = () => {
    SecurityService.twoFaRegenerate().then((resp) => {
      setRegeneratedBackupCodes(resp?.data?.backup_codes ?? []);
      setIsRegenerateCodesModalOpen(true);
    });
  };

  const handleCloseRegeneratedCodes = () => {
    setIsRegenerateCodesModalOpen(false);
    setRegeneratedBackupCodes([]);
  };

  const handleTwoFAOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    twoFAHandlePaste(e, (pastedText) => {
      handleUnlock2FA(pastedText);
    });
  };

  const fetch2FAStatus = () => {
    setIs2FALoading(true);

    SecurityService.twoFaStatus().then((resp) => {
      if (typeof resp?.data?.locked === "boolean") {
        setIs2FALocked(resp.data.locked);
      }

      if (typeof resp?.data?.enabled === "boolean") {
        setIs2FAEnabled(resp.data.enabled);
      }

      setIs2FALoading(false);
    });
  };

  const handleEnterKeyPress = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    twoFAHandleKeyDown(index, e);

    if (e.key.match(/^[0-9]$/)) {
      const updatedPin = [...twoFAPin];
      updatedPin[index] = e.key;

      if (updatedPin.join("").length === 6) {
        handleUnlock2FA(updatedPin.join(""));
      }
    }
  };

  const refetchSecuritySessions = () => {
    ProfileService.fetchSecuritySessions()
      .then((res) => {
        setDevices(res.data.items.slice().reverse());
      })
      .catch(() => {
        notification.error({ message: "Something went wrong." });
      });
  };

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  useEffect(() => {
    setIs2FaChecked(is2FAEnabled);
  }, [is2FAEnabled]);

  useEffect(() => {
    Promise.all([
      ProfileService.fetchSecuritySessions(),
      ProfileService.fetchSecurityActivities(),
    ]).then((res) => {
      setDevices(res[0].data.items.slice().reverse());
      setActivities(res[1].data.items);
    });
  }, []);

  return (
    <>
      <div>
        <SectionWrapper
          title={
            <div className="flex flex-col-reverse items-start lg:flex-row lg:items-center gap-4">
              <span>2 FACTOR AUTHENTICATION (2FA)</span>
              <span className="text-[11px] text-white bg-[#81109C] uppercase px-3 rounded-full">
                RECOMMENDED
              </span>
            </div>
          }
          sectionAction={
            <Switch checked={is2FaChecked} onChange={handleToggle2Fa} />
          }
        >
          {is2FALoading ? (
            <Loader />
          ) : (
            <>
              {is2FALocked && (
                <div className="flex flex-col items-center gap-8">
                  <UnlockIcon />
                  <div>Enter a valid 2FA token to unlock access</div>
                  <div className="flex gap-2 justify-center">
                    {twoFAPin.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (twoFAInputRefs.current[index] = el)}
                        value={digit}
                        maxLength={1}
                        onChange={(e) =>
                          twoFAHandleChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleEnterKeyPress(index, e)}
                        onPaste={handleTwoFAOnPaste}
                        className="text-center text-lg font-semibold border border-grey-lighter tailwind-2fa-input"
                      />
                    ))}
                  </div>

                  <Button
                    disabled={twoFAPin.join("").length < 6}
                    isPrimary
                    onClick={() => handleUnlock2FA()}
                  >
                    Unlock
                  </Button>
                </div>
              )}

              {!is2FALocked && (
                <>
                  <p>
                    We strongly recommend you enable Two Factor Authentication
                    (2FA) for your account for your added security
                  </p>

                  <p>
                    To use 2FA you will have to install an Authenticator app on
                    your smartphone or tablet. Once enabled you will be asked to
                    provide an additional six-digit one-time password along with
                    your email and password every time you login to your BF
                    account. This works only when signing in with email and
                    password. When using Facebook or LinkedIn you can enable 2FA
                    with your social media provider settings.
                  </p>
                </>
              )}
            </>
          )}
        </SectionWrapper>

        {!is2FALocked && is2FAEnabled && (
          <SectionWrapper
            sectionAction={
              <Button onClick={handleRegenerateCodes}>Regenerate</Button>
            }
            title="Backup codes for 2FA"
          >
            <p className="mb-0">
              If you lose access to your authentication device, you can use one
              of these backup codes to login to your account. Each code may be
              used only once. Make a copy of these codes, and store it somewhere
              safe.
            </p>
          </SectionWrapper>
        )}

        <SectionWrapper title="Password">
          <p>
            Your password must include at least one lowercase letter, one
            uppercase letter, one number, and one special character, with a
            minimum length of six characters.
          </p>

          <div className="flex justify-between items-center gap-3 py-2">
            <div>
              <div className="text-xs uppercase">Password</div>
              <div>********</div>
            </div>

            <Button onClick={() => setIsChangePasswordOpen(true)}>
              Change password
            </Button>
          </div>
        </SectionWrapper>

        <SectionWrapper title="Account login activity">
          <div className="flex flex-col gap-4">
            {[...devices]
              .sort((a, b) => Number(b.online) - Number(a.online))
              .map((device) => {
                const browserData = device.browser_data;

                return (
                  <div className="flex gap-4 p-8 bg-grey-light rounded-[20px]">
                    {browserData.device_type === "desktop" ? (
                      <DesktopIcon />
                    ) : (
                      <MobileIcon />
                    )}

                    <div className="text-sm">
                      <div className="lg:mb-8 mb-4">
                        <div>
                          {browserData.browser_name} on {browserData.os_name}{" "}
                          {browserData.os_version}
                        </div>

                        <div
                          className={classNames({
                            "text-[#2D9C5E]": device.online,
                            "text-grey-lightest": !device.online,
                          })}
                        >
                          {device.online
                            ? "Your current session"
                            : `Currently accessed on ${moment(
                                device.last_time_used
                              ).format("MMM DD, YYYY")}`}
                        </div>
                      </div>

                      <div className="flex lg:flex-row flex-col">
                        <div className="lg:pr-8">{device.country}</div>

                        <div className="lg:px-8 lg:border-l lg:border-r border-[#CACACA]">
                          {device.ip}
                        </div>

                        <div className="lg:px-8">
                          {moment(device.last_time_used).format("MMM DD, YYYY")}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </SectionWrapper>

        <SectionWrapper title="Security history">
          <Table
            className="tailwind-table"
            columns={securityActivitiesColumns}
            dataSource={securityHistoryData.slice(0, visibleItems)}
            pagination={false}
          />

          {securityHistoryData.length > visibleItems && (
            <div className="flex justify-center mt-8">
              <Button
                className="flex items-center gap-2 border-[#2EC973]"
                onClick={() => setVisibleItems(securityHistoryData.length)}
              >
                <span>Load more</span>
                <ChevronDownIcon />
              </Button>
            </div>
          )}
        </SectionWrapper>
      </div>

      <TwoFAModal
        open={is2FAModdalOpen}
        onClose={() => setIs2FAModdalOpen(false)}
        fetch2FAStatus={fetch2FAStatus}
      />

      <TwoFABackupModal
        open={isRegenerateCodesModalOpen}
        onClose={handleCloseRegeneratedCodes}
        backupCodes={regeneratedBackupCodes}
      />

      <ChangePasswordModal
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
        refetchSecuritySessions={refetchSecuritySessions}
      />
    </>
  );
};
